<template>
  <b-card>
    <b-form>
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('general.title')"
            label-for="page-category-title"
          >
            <b-form-input
              id="page-category-title"
              v-model="form.title"
              :state="validateState('title')"
              aria-describedby="input-title-feedback"
              @change="updateAltImage()"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.slug')"
            label-for="page-category-slug"
          >
            <b-form-input
              id="page-category-slug"
              v-model="form.slug"
              :state="validateState('slug')"
              aria-describedby="input-slug-feedback"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('general.slug')"
              :validator="$v.form.slug"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.language')"
            label-for="page-category-language"
          >
            <languageSelector v-model="form.lang" />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.keywords')"
            label-for="page-category-keywords"
          >
            <b-form-input
              id="page-category-keywords"
              v-model="form.keywords"
              :state="validateState('keywords')"
              aria-describedby="input-keywords-feedback"
            />
            <b-form-invalid
              id="input-keywords-feedback"
              :label="$t('general.keywords')"
              :validator="$v.form.keywords"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.description')"
            label-for="page-category-description"
          >
            <b-form-textarea
              id="page-category-description"
              v-model="form.description"
              :state="validateState('description')"
              rows="6"
              aria-describedby="input-description-feedback"
            />
            <b-form-invalid
              id="input-description-feedback"
              :label="$t('general.description')"
              :validator="$v.form.description"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <div class="border rounded p-2">
            <h4 class="mb-1">
              {{ $t("general.thumbnail") }} - {{ $t("general.acceptImage") }}
            </h4>
            <b-file-input
              id="pageCatThumbnail"
              v-model="form.pageCatThumbnail"
              :state="validateState('pageCatThumbnail')"
              :alt.sync="form.pageCatThumbnailAlt"
              :alt-validator="$v.form.pageCatThumbnailAlt"
            >
              <template #invalidFeedback>
                <b-form-invalid
                  id="input-pageCatThumbnail-feedback"
                  :label="$t('pageCategories.pageCatThumbnail')"
                  :validator="$v.form.pageCatThumbnail"
                />
              </template>
            </b-file-input>
          </div>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <div class="border rounded p-2">
            <h4 class="mb-1">
              {{ $t("general.cover") }} - {{ $t("general.acceptImage") }}
            </h4>
            <b-file-input
              id="pageCatCoverImage"
              v-model="form.pageCatCoverImage"
              :state="validateState('pageCatCoverImage')"
              :alt.sync="form.pageCatCoverImageAlt"
              :alt-validator="$v.form.pageCatCoverImageAlt"
            >
              <template #invalidFeedback>
                <b-form-invalid
                  id="input-pageCatCoverImage-feedback"
                  :label="$t('pageCategories.pageCatCoverImage')"
                  :validator="$v.form.pageCatCoverImage"
                />
              </template>
            </b-file-input>
          </div>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.update") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>

</template>

<script>
import {
  BButton, BCard, BCol, BForm, BFormGroup, BFormInput, BFormTextarea, BRow,
} from 'bootstrap-vue'
import BFileInput from '@/components/BFileInput/index.vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import Ripple from 'vue-ripple-directive'
import languageSelector from '@/components/language-selector.vue'
import { isValidImageSize } from '@core/utils/rules'
import { required, minLength, requiredIf } from 'vuelidate/lib/validators'
import usePageCategoriesList from './usePageCategoriesList'

export default {
  name: 'PageCategoryAdd',
  components: {
    BButton,
    BCard,
    BCol,
    BFileInput,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormTextarea,
    BRow,
    languageSelector,
  },
  directives: {
    Ripple,
  },
  validations: {
    form: {
      title: {
        required,
        minLength: minLength(3),
      },
      slug: {
        required,
        minLength: minLength(3),
      },
      keywords: {
        required,
        minLength: minLength(3),
      },
      description: {
        required,
        minLength: minLength(10),
      },
      pageCatThumbnail: {
        required,
        isTrueImage: requiredIf(function () {
          return typeof this.form.pageCatThumbnail === 'object' && !Object.prototype.hasOwnProperty.call(this.form.pageCatThumbnail, '_id')
        }),
        isValidImageSize,
      },
      pageCatCoverImage: {
        required,
        isTrueImage: requiredIf(function () {
          return typeof this.form.pageCatCoverImage === 'object' && !Object.prototype.hasOwnProperty.call(this.form.pageCatCoverImage, '_id')
        }),
        isValidImageSize,
      },
      pageCatThumbnailAlt: {
        required,
        minLength: minLength(3),
      },
      pageCatCoverImageAlt: {
        required,
        minLength: minLength(3),
      },
    },
  },
  setup() {
    const {
      addItem,
      errorManager,
      fetchItem,
      form,
      handleUpload,
      isSubmit,
      item,
      updateItem,
    } = usePageCategoriesList()
    return {
      addItem,
      errorManager,
      fetchItem,
      form,
      handleUpload,
      isSubmit,
      item,
      updateItem,
    }
  },
  watch: {
    'form.title': function (newValue, oldValue) {
      this.$set(this.form, 'slug', this.createSlug(this.form.title))
      if (oldValue === this.form.pageCatThumbnailAlt) {
        this.$set(this.form, 'pageCatThumbnailAlt', newValue)
      }
      if (oldValue === this.form.pageCatCoverImageAlt) {
        this.$set(this.form, 'pageCatCoverImageAlt', newValue)
      }
    },
  },
  created() {
    this.form.lang = 'fa'
  },
  methods: {
    updateAltImage() {
      if (!this.form.pageCatCoverImageAlt) {
        this.$set(this.form, 'pageCatCoverImageAlt', this.form.title)
      }
      if (!this.form.pageCatThumbnailAlt) {
        this.$set(this.form, 'pageCatThumbnailAlt', this.form.title)
      }
    },
    validateState(name) {
      const {
        $dirty,
        $error,
      } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    submitForm() {
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        }), 500)
        return
      }
      const uploadList = ['pageCatCoverImage', 'pageCatThumbnail']
      this.handleUpload(this.form, uploadList).then(newForm => {
        this.sendData(newForm)
      })
    },
    sendData(form) {
      const data = {
        title: form.title,
        slug: form.slug,
        keywords: form.keywords,
        description: form.description,
        pageCatCoverImageFileId: form.pageCatCoverImage ? form.pageCatCoverImage._id : null,
        pageCatThumbnailFileId: form.pageCatThumbnail ? form.pageCatThumbnail._id : null,
      }
      this.addItem(data, this.form.lang)
    },
  },
}
</script>
